// Auth context over Firebase Auth (oneselfpath-dev Identity Platform). Email/password
// with required email verification, Google sign-in, password reset by email, and an
// in-app password change. Session persistence is Firebase's; nothing auth-related is
// written to localStorage by this app.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updatePassword,
  type User as FirebaseUser,
} from "firebase/auth";
import { IS_MOCK_AUTH, getFirebaseAuth } from "@/lib/firebase";

const MOCK_USER = {
  uid: "mock-patient-uid",
  email: "alex.rivera@example.com",
  displayName: "Alex Rivera",
  emailVerified: true,
  isAnonymous: false,
  metadata: {},
  providerData: [],
  refreshToken: "",
  tenantId: null,
  delete: async () => {},
  getIdToken: async () => "mock-id-token",
  getIdTokenResult: async () => ({ claims: {} }) as never,
  reload: async () => {},
  toJSON: () => ({}),
  phoneNumber: null,
  photoURL: null,
  providerId: "mock",
} as unknown as FirebaseUser;

export interface AuthContext {
  user: FirebaseUser | null;
  /** True until Firebase has reported the initial auth state. */
  loading: boolean;
  isAuthenticated: boolean;
  /** Email/password accounts must verify before the app lets them past the login screen. */
  emailVerified: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  /** Creates the account and sends the verification email. The caller registers the profile with the API. */
  signUp: (email: string, password: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  /** Re-reads the user from Firebase (after they click the verification link). */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const Ctx = createContext<AuthContext | null>(null);

/** Firebase error codes → something a patient can act on. */
export function authErrorMessage(err: unknown): string {
  const code = typeof err === "object" && err !== null && "code" in err ? String((err as { code: unknown }).code) : "";
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Login failed. Please check your email and password.";
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/weak-password":
      return "Please choose a stronger password.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a few minutes and try again.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Sign-in was cancelled.";
    case "auth/requires-recent-login":
      return "Please sign in again before changing your password.";
    case "auth/network-request-failed":
      return "Network problem. Please check your connection and try again.";
    default:
      return err instanceof Error && err.message ? err.message : "Something went wrong.";
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(IS_MOCK_AUTH ? MOCK_USER : null);
  const [loading, setLoading] = useState(!IS_MOCK_AUTH);
  // Firebase mutates the User in place; bump this to re-render after reload().
  const [, setTick] = useState(0);

  useEffect(() => {
    if (IS_MOCK_AUTH) return;
    return onAuthStateChanged(getFirebaseAuth(), (u) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  const value: AuthContext = {
    user,
    loading,
    isAuthenticated: !!user,
    emailVerified: !!user && (IS_MOCK_AUTH || user.emailVerified || user.providerData.some((p) => p.providerId !== "password")),

    signIn: async (email, password) => {
      if (IS_MOCK_AUTH) return void setUser(MOCK_USER);
      await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
    },
    signInWithGoogle: async () => {
      if (IS_MOCK_AUTH) return void setUser(MOCK_USER);
      await signInWithPopup(getFirebaseAuth(), new GoogleAuthProvider());
    },
    signUp: async (email, password) => {
      if (IS_MOCK_AUTH) return void setUser({ ...MOCK_USER, email } as FirebaseUser);
      const cred = await createUserWithEmailAndPassword(getFirebaseAuth(), email, password);
      await sendEmailVerification(cred.user);
    },
    resendVerification: async () => {
      if (IS_MOCK_AUTH) return;
      const u = getFirebaseAuth().currentUser;
      if (u) await sendEmailVerification(u);
    },
    refresh: async () => {
      if (IS_MOCK_AUTH) return;
      const u = getFirebaseAuth().currentUser;
      if (u) {
        await u.reload();
        await u.getIdToken(true);
        setTick((t) => t + 1);
      }
    },
    signOut: async () => {
      if (IS_MOCK_AUTH) return void setUser(null);
      await firebaseSignOut(getFirebaseAuth());
    },
    resetPassword: async (email) => {
      if (IS_MOCK_AUTH) return;
      await sendPasswordResetEmail(getFirebaseAuth(), email);
    },
    changePassword: async (currentPassword, newPassword) => {
      if (IS_MOCK_AUTH) return;
      const u = getFirebaseAuth().currentUser;
      if (!u?.email) throw new Error("You are signed out. Sign in and try again.");
      await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email, currentPassword));
      await updatePassword(u, newPassword);
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
